import { useLocation, useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { useStudioStore } from '@/store'
import { confirmLeaveUnsavedChanges } from '@/lib/unsavedChanges'
import { BarChart3, BookOpenCheck, Target, TrendingUp, Trophy, Wallet, Megaphone, UsersRound } from 'lucide-react'

export function StudioNavigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedStudio, selectedPartnerNumber } = useStudioStore()
  const params = useParams<{ partnerNumber?: string }>()

  if (!selectedStudio) return null

  const studioId = selectedStudio.studio_internal_id
  const partnerNumber = params.partnerNumber || (selectedPartnerNumber != null ? String(selectedPartnerNumber) : null)
  if (!partnerNumber) return null
  const isSeniorTeacher = selectedStudio.access_kind === 'senior_teacher' || selectedStudio.role === 'senior_teacher'
  const isTeacher = selectedStudio.role === 'teacher' && !isSeniorTeacher
  const canAccessMarketing = selectedStudio.role === 'owner'
    || selectedStudio.role === 'manager'
    || selectedStudio.access_kind === 'management_company'
  const canViewTeachers = isTeacher
    || selectedStudio.access_kind === 'owner'
    || selectedStudio.role === 'manager'
    || selectedStudio.access_kind === 'management_company'
    || isSeniorTeacher
  const basePath = `/partners/${partnerNumber}/studios/${studioId}`
  const partnerMarketingPath = `/partners/${partnerNumber}/marketing`

  const navItems = [
    {
      path: `${basePath}/metrics`,
      icon: BarChart3,
      label: 'Показатели',
      enabled: !isSeniorTeacher && !isTeacher
    },
    {
      path: `${basePath}/analytics`,
      icon: TrendingUp,
      label: 'Аналитика',
      enabled: !isSeniorTeacher && !isTeacher
    },
    {
      path: `${basePath}/clients`,
      icon: UsersRound,
      label: 'Клиенты',
      enabled: !isSeniorTeacher && !isTeacher
    },
    {
      path: partnerMarketingPath,
      icon: Megaphone,
      label: 'Маркетинг',
      enabled: canAccessMarketing
    },
    {
      path: `${basePath}/ratings`,
      icon: Trophy,
      label: 'Рейтинги',
      enabled: !isSeniorTeacher && !isTeacher
    },
    {
      path: `${basePath}/teachers`,
      icon: BookOpenCheck,
      label: 'Педагоги',
      enabled: canViewTeachers
    },
    {
      path: `${basePath}/studio-metrics-grid`,
      icon: Target,
      label: 'План',
      enabled: !isSeniorTeacher && !isTeacher
    },
    {
      path: `${basePath}/finance`,
      icon: Wallet,
      label: 'ДДС/PnL',
      enabled: !isSeniorTeacher && !isTeacher && selectedStudio.role !== 'member'
    }
  ]

  return (
    <div className="tg-navigation sticky bottom-0 z-30 bg-card border-t border-border px-4 py-2">
      <div className="flex justify-around">
        {navItems.map((item) => {
          if (!item.enabled) return null
          
          const Icon = item.icon
          const isMarketingItem = item.label === 'Маркетинг'
          const isPlanItem = item.label === 'План'
          const isTeachersItem = item.label === 'Педагоги'
          const isActive = isMarketingItem
            ? /^\/partners\/[^/]+\/marketing(\/.*)?$/.test(location.pathname)
            : isPlanItem
              ? /^\/partners\/[^/]+\/studios\/[^/]+\/studio-metrics(-grid)?$/.test(location.pathname)
              : isTeachersItem
                ? /^\/partners\/[^/]+\/studios\/[^/]+\/(teachers(?:\/.*)?|trainer-reviews)$/.test(location.pathname)
              : location.pathname === item.path
          
          return (
            <button
              key={item.path}
              onClick={() => {
                if (location.pathname === item.path) return
                if (!confirmLeaveUnsavedChanges()) return
                navigate(item.path)
              }}
              className={`
                flex flex-col items-center p-2 rounded-lg transition-colors
                ${isActive 
                  ? 'text-primary bg-primary/10' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="text-xs">{item.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
